import { useEffect, useRef, useState, type RefObject } from "react";
import type { Feature, Polygon } from "geojson";
import mapboxgl from "mapbox-gl";
import { ChevronDown } from "lucide-react";
import { ExpandableMapFrame } from "@/components/common/ExpandableMapFrame";
import { useEmbeddedMap, type MapCamera } from "@/hooks/useEmbeddedMap";
import { formatDateTime } from "@/lib/datetime";
import {
  readDeviceCoordinates,
  type DeviceCoordinates,
} from "@/lib/deviceStatus";
import { getMapboxPublicToken } from "@/lib/mapboxConfig";
import type { Device } from "@/types";

export type DeviceTelemetryLabels = {
  title: string;
  lastReport: string;
  sectionDeviceStatus: string;
  sectionGpsLocation: string;
  neverReceived: string;
  emptyPayload: string;
};

type Props = {
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >;
  labels: DeviceTelemetryLabels;
};

const DEVICE_LOCATION_ZOOM = 14;
const EARTH_RADIUS_METERS = 6_371_000;
const ACCURACY_CIRCLE_STEPS = 64;

function formatReportTime(
  iso: string | null | undefined,
  neverLabel: string,
): string {
  if (iso == null || iso === "") return neverLabel;
  return formatDateTime(iso);
}

function JsonBlock({
  data,
  emptyLabel,
}: {
  data: unknown;
  emptyLabel: string;
}) {
  if (data === null || data === undefined) {
    return <p className="text-sm text-surface-500">{emptyLabel}</p>;
  }
  return (
    <pre className="max-h-56 overflow-auto rounded-lg border border-surface-200 bg-surface-50 p-3 font-mono text-xs leading-relaxed text-surface-700">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function buildAccuracyCircleGeoJson(
  coordinates: DeviceCoordinates,
): Feature<Polygon> | null {
  const radiusMeters = coordinates.accuracyMeters;
  if (radiusMeters === null) {
    return null;
  }

  const centerLatitudeRadians = (coordinates.latitude * Math.PI) / 180;
  const centerLongitudeRadians = (coordinates.longitude * Math.PI) / 180;
  const angularDistance = radiusMeters / EARTH_RADIUS_METERS;
  const points: [number, number][] = [];

  for (let index = 0; index <= ACCURACY_CIRCLE_STEPS; index += 1) {
    const bearing = (2 * Math.PI * index) / ACCURACY_CIRCLE_STEPS;
    const latitudeRadians = Math.asin(
      Math.sin(centerLatitudeRadians) * Math.cos(angularDistance) +
        Math.cos(centerLatitudeRadians) *
          Math.sin(angularDistance) *
          Math.cos(bearing),
    );
    const longitudeRadians =
      centerLongitudeRadians +
      Math.atan2(
        Math.sin(bearing) *
          Math.sin(angularDistance) *
          Math.cos(centerLatitudeRadians),
        Math.cos(angularDistance) -
          Math.sin(centerLatitudeRadians) * Math.sin(latitudeRadians),
      );

    points.push([
      (longitudeRadians * 180) / Math.PI,
      (latitudeRadians * 180) / Math.PI,
    ]);
  }

  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [points],
    },
  };
}

const ACCURACY_SOURCE_ID = "device-accuracy-circle";

function addAccuracyCircle(map: mapboxgl.Map, circle: Feature<Polygon>) {
  map.addSource(ACCURACY_SOURCE_ID, { type: "geojson", data: circle });
  map.addLayer({
    id: `${ACCURACY_SOURCE_ID}-fill`,
    type: "fill",
    source: ACCURACY_SOURCE_ID,
    paint: { "fill-color": "#2563eb", "fill-opacity": 0.12 },
  });
  map.addLayer({
    id: `${ACCURACY_SOURCE_ID}-outline`,
    type: "line",
    source: ACCURACY_SOURCE_ID,
    paint: { "line-color": "#2563eb", "line-opacity": 0.45, "line-width": 2 },
  });
}

/** Keyed by the fix it shows: a new fix remounts it, so layers are added once per map. */
function DeviceLocationCanvas({
  coordinates,
  cameraRef,
  cooperativeGestures,
}: {
  coordinates: DeviceCoordinates;
  cameraRef: RefObject<MapCamera | null>;
  cooperativeGestures: boolean;
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { latitude, longitude, accuracyMeters } = coordinates;
  const map = useEmbeddedMap({
    container,
    cameraRef,
    initialCamera: { center: [longitude, latitude], zoom: DEVICE_LOCATION_ZOOM },
    cooperativeGestures,
  });

  useEffect(() => {
    if (!map) return;
    const marker = new mapboxgl.Marker().setLngLat([longitude, latitude]).addTo(map);
    return () => {
      marker.remove();
    };
  }, [map, latitude, longitude]);

  useEffect(() => {
    if (!map) return;
    const circle = buildAccuracyCircleGeoJson({ latitude, longitude, accuracyMeters });
    if (!circle) return;
    const draw = () => addAccuracyCircle(map, circle);
    if (map.isStyleLoaded()) draw();
    else map.once("load", draw);
    return () => {
      map.off("load", draw);
    };
  }, [map, latitude, longitude, accuracyMeters]);

  return <div ref={setContainer} className="h-full w-full" />;
}

function DeviceLocationMap({
  coordinates,
}: {
  coordinates: DeviceCoordinates;
}) {
  const cameraRef = useRef<MapCamera | null>(null);

  if (!getMapboxPublicToken()) {
    return null;
  }

  return (
    <ExpandableMapFrame
      className="rounded-lg border border-surface-200"
      mapClassName="h-48"
    >
      {(expanded) => (
        <DeviceLocationCanvas
          key={`${coordinates.latitude},${coordinates.longitude},${coordinates.accuracyMeters}`}
          coordinates={coordinates}
          cameraRef={cameraRef}
          cooperativeGestures={!expanded}
        />
      )}
    </ExpandableMapFrame>
  );
}

/** Read-only telemetry from tablet sync (FreeKiosk status + GPS). */
export function DeviceTelemetryPanel({ device, labels }: Props) {
  const coordinates = readDeviceCoordinates(device.device_location_payload);

  return (
    <div className="col-span-full mt-5 space-y-4 border-t border-surface-200 pt-5">
      {coordinates ? <DeviceLocationMap coordinates={coordinates} /> : null}
      <details className="group rounded-lg border border-surface-200 bg-surface-0">
        <summary className="flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg px-3.5 py-2.5 text-sm font-medium text-surface-700 hover:bg-surface-50 hover:text-surface-900 dark:hover:bg-surface-100/50 [&::-webkit-details-marker]:hidden">
          {labels.title}
          <ChevronDown
            className="h-4 w-4 shrink-0 text-surface-400 transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="space-y-4 border-t border-surface-200 px-3.5 py-3.5">
          <div>
            <p className="text-xs font-medium text-surface-500">{labels.lastReport}</p>
            <p className="mt-0.5 text-sm tabular-nums text-surface-900">
              {formatReportTime(
                device.last_device_report_at,
                labels.neverReceived,
              )}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium text-surface-500">
              {labels.sectionDeviceStatus}
            </p>
            <JsonBlock
              data={device.device_status_payload}
              emptyLabel={labels.emptyPayload}
            />
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium text-surface-500">
              {labels.sectionGpsLocation}
            </p>
            <JsonBlock
              data={device.device_location_payload}
              emptyLabel={labels.emptyPayload}
            />
          </div>
        </div>
      </details>
    </div>
  );
}
